import { useState, useEffect, useRef, useContext } from "react";
import ReactDOM from "react-dom";
import { Theme } from "@radix-ui/themes";
import { ChevronLeft, ChevronRight, X, Car } from "lucide-react";
import { GlobalContext } from "./GlobalContext";
import { chargesDemurrage, defaultLocationName, defaultStorageType, storageLocations, usesBothStorageTypes } from "./validation/storage";
import ModalTitleBar from "./ModalTitleBar";
import { apiFetch } from "./api.js";
import { TOUR_ANCHORS } from "./tourAnchors";
import VehicleIdentitySteps, {
  useVehicleIdentity, identityToFields, StepIndicator,
} from "./wizard/VehicleIdentitySteps";
import { ChassisComposer, ModelCodeWarning, useModelCodeCheck } from "./components/ChassisNumber";
import { bareModelCode } from "./carmakesMap";

// ── Categorised feature options ──────────────────────────────────────────────

const FEATURES = {
  Safety: [
    "ABS", "EBD", "Airbags", "Side Airbags", "Curtain Airbags",
    "ESC", "Traction Ctrl", "Rev. Camera", "Park. Sensors", "Pretensioners",
  ],
  Exterior: [
    "Alloy Wheels", "Sunroof", "Moonroof", "Panoramic Roof",
    "Fog Lights", "LED Headlights", "HID Headlights", "Auto Headlights",
    "Spoiler", "Side Steps", "Roof Rack",
  ],
  Comfort: [
    "AC", "PS", "PW", "Tilt Steer.", "Cruise Control",
    "Leather Seats", "Heated Seats", "Keyless Entry", "Push Start",
    "Elec. Mirrors", "Elec. Seats", "Dual Climate",
  ],
  Audio: [
    "Navigation", "Bluetooth", "USB", "DVD",
    "CarPlay", "Android Auto", "Rev. Screen",
  ],
};

// Fold the wizard's four groups into one fields.js-keyed vehicle record for
// AddEditInventory to open with. Steps 1-4 arrive already translated
// (identityToFields); the rest are this component's own form state.
function buildPrefill(identity, details, purchase, shipping) {
  //ModelCode is not an Inventory column and never reaches a vehicle record. It
  //used to arrive here as `ChassisNo`, which is how a model code came to be saved
  //as a chassis number — `NZE141G` names the model, not the car
  //(VehicleIdentitySteps.identityToFields). It is now offered as a PREFIX on step
  //5 and comes back as `details.chassisNo`, already composed with its serial.
  //
  //Still absent when the serial was left blank, which is correct: ChassisNo is
  //required, so AddEditInventory asks for it and it gets looked at. A wrong one
  //that passes the length rule never gets a second glance.
  const { ModelCode, ...identityFields } = identity;
  const result = {
    ...identityFields,
    // Step 5 — Vehicle Details
    ...(details.chassisNo   && { ChassisNo:   details.chassisNo }),
    Steering: details.steering || "RHD",
    ...(details.color       && { Color:       details.color }),
    ...(details.mileage     && { Mileage:     parseInt(details.mileage, 10) }),
    ...(details.numberPlate && { NumberPlate: details.numberPlate }),
    ...(details.features    && { Features:    details.features }),
    // Step 6 — Purchase
    PurchaseDate:     purchase.purchaseDate,
    PurchasePrice:    purchase.purchasePrice ? parseFloat(purchase.purchasePrice) : 0,
    PurchaseCurrency: purchase.purchaseCurrency,
    ExchangeRate:     purchase.purchaseCurrency === "UGX"
                        ? 1
                        : (purchase.exchangeRate ? parseFloat(purchase.exchangeRate) : 0),
    ...(purchase.invoiceNo && { InvoiceNo: purchase.invoiceNo }),
  };
  // Step 7 — Shipping (Imported only)
  if (shipping) {
    const s = shipping;
    if (s.blNo)              result.BLNo              = s.blNo;
    if (s.shippingDate)      result.ShippingDate      = s.shippingDate;
    if (s.shippingCost)      result.ShippingCost      = parseFloat(s.shippingCost);
    if (s.etaMombasa)        result.EtaMombasa        = s.etaMombasa;
    if (s.landTranCost)      result.LandTranCost      = parseFloat(s.landTranCost);
    if (s.etaKla)            result.EtaKla            = s.etaKla;
    if (s.clearingCost)      result.ClearingCost      = parseFloat(s.clearingCost);
    if (s.clearingUG)        result.ClearingUG        = parseFloat(s.clearingUG);
    result.WarehouseDate     = s.warehouseDate;
    result.BondName          = s.bondName;
    //Carried so the vehicle records which KIND of place that name is. Without
    //it the row lands with StorageType empty, which makes DailyDemurrage's
    //requiredWhen false and quietly stops asking for a rate that may be owed.
    result.StorageType       = s.storageType;
    //Only when a rate applies. A blank into a DECIMAL column is refused outright
    //by the deal procedures, so the key is DELETED rather than sent empty.
    if (s.dailyDemurrage !== "" && s.dailyDemurrage != null) {
      result.DailyDemurrage  = parseFloat(s.dailyDemurrage);
    }
    if (s.duty)              result.Duty              = parseFloat(s.duty);
    if (s.targetExportPrice) result.TargetExportPrice = parseFloat(s.targetExportPrice);
  }
  return result;
}

// Labelled field wrapper used in steps 5–7
function FieldRow({ label, required, children }) {
  return (
    <div>
      <label className="label-t block mb-0.5">
        {label}
        {required
          ? <span className="error-t ml-0.5">*</span>
          : <span className="hint-t font-normal ml-1">(opt.)</span>}
      </label>
      {children}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function VehiclePickerModal({ origin, onComplete, onSkip, onClose }) {
  const { companyData } = useContext(GlobalContext);

  const [step, setStep]         = useState(1);

  //Steps 1-4 (Make → Model → Variant → Year) live in wizard/VehicleIdentitySteps
  //so the sold/reserved-vehicle wizard can use the same four rather than a copy.
  //The hook advances the step; everything from 5 on is still this component's.
  const identity = useVehicleIdentity(setStep);
  const { error, clearSearch } = identity;

  //The chassis number, built from the catalogue's model code rather than typed
  //whole. Steps 1-4 know the code; once this modal hands off to
  //AddEditInventory that context is gone, so this is the last screen where the
  //serial can be asked for cheaply.
  const [chassisNo,        setChassisNo]        = useState("");
  const modelCodeOnly = useModelCodeCheck(chassisNo);

  const [steering,         setSteering]         = useState("RHD");
  const [color,            setColor]            = useState("");
  const [mileage,          setMileage]          = useState("");
  const [numberPlate,      setNumberPlate]      = useState("");
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [otherFeatures,    setOtherFeatures]    = useState("");
  const [featureTab,       setFeatureTab]       = useState("Safety");

  // Step 6 — Purchase
  const today = new Date().toISOString().split("T")[0];
  const [purchaseDate,     setPurchaseDate]     = useState(today);
  const [purchasePrice,    setPurchasePrice]    = useState("");
  const [purchaseCurrency, setPurchaseCurrency] = useState(origin === "Local" ? "UGX" : "USD");
  const [exchangeRate,     setExchangeRate]     = useState(origin === "Local" ? "1" : "");
  const [invoiceNo,        setInvoiceNo]        = useState("");

  // Step 7 — Shipping (Imported only)
  // BondName and DailyDemurrage are required; pre-seed from dealership settings
  const [blNo,              setBlNo]              = useState("");
  const [shippingDate,      setShippingDate]      = useState(today);
  const [shippingCost,      setShippingCost]      = useState("");
  const [etaMombasa,        setEtaMombasa]        = useState(today);
  const [landTranCost,      setLandTranCost]      = useState("");
  const [etaKla,            setEtaKla]            = useState(today);
  const [clearingCost,      setClearingCost]      = useState("");
  const [clearingUG,        setClearingUG]        = useState("");
  const [warehouseDate,     setWarehouseDate]     = useState(today);
  const [bondName,          setBondName]          = useState(
    defaultLocationName(companyData?.StorageSettings) || companyData?.BondName || "");
  //Which kind of place that is. Seeded from the dealership's settings; asked only
  //when the dealership uses both kinds (see the step below).
  const [storageType,       setStorageType]       = useState(
    defaultStorageType(companyData?.StorageSettings));
  const [dailyDemurrage,    setDailyDemurrage]    = useState(
    chargesDemurrage({ StorageType: defaultStorageType(companyData?.StorageSettings) },
                     companyData?.StorageSettings)
      ? (companyData?.DailyPenalty?.toString() || "")
      : "");
  const [duty,              setDuty]              = useState("");
  const [targetExportPrice, setTargetExportPrice] = useState("");

  //Does a demurrage rate apply to the vehicle being entered? Driven by the
  //control above rather than by the dealership alone, so switching it shows and
  //hides the rate on the spot.
  const showDemurrage = chargesDemurrage({ StorageType: storageType }, companyData?.StorageSettings);
  const asksStorageKind = usesBothStorageTypes(companyData?.StorageSettings);
  const locationSuggestions = storageLocations(companyData?.StorageSettings, storageType)
    .map((l) => l.name);

  //Empty when the variant was skipped, which is exactly when ChassisComposer
  //falls back to a plain box.
  const chassisPrefix = identity.selectedVariant && identity.selectedVariant !== "skip"
    ? bareModelCode(identity.selectedVariant.model_code)
    : "";

  const totalSteps = origin === "Imported" ? 7 : 6;

  const stepTitles = [
    "Choose Make", "Choose Model", "Choose Variant", "Confirm Year",
    "Vehicle Details", "Purchase Info",
    ...(origin === "Imported" ? ["Shipping & Logistics"] : []),
  ];

  //Clearing the search on Back is what makes step 2 open on the full model list
  //rather than filtered by whatever was typed on step 1.
  const goBack = () => { clearSearch(); setStep(s => s - 1); };

  const toggleFeature = f =>
    setSelectedFeatures(fs => fs.includes(f) ? fs.filter(x => x !== f) : [...fs, f]);

  const finalize = () => {
    const featuresArr = [
      ...selectedFeatures,
      ...(otherFeatures.trim() ? [otherFeatures.trim()] : []),
    ];
    const featuresStr = featuresArr.join(", ").slice(0, 100);

    onComplete(buildPrefill(
      identityToFields(identity),
      { steering, color, mileage, numberPlate, features: featuresStr, chassisNo },
      { purchaseDate, purchasePrice, purchaseCurrency, exchangeRate, invoiceNo },
      origin === "Imported"
        ? { blNo, shippingDate, shippingCost, etaMombasa, landTranCost, etaKla,
            clearingCost, clearingUG, warehouseDate, bondName, storageType, dailyDemurrage,
            duty, targetExportPrice }
        : null,
    ));
  };

  const handleContinue = () => {
    if (step < totalSteps) setStep(s => s + 1);
    else finalize();
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const canContinue = () => {
    //A model code is refused even though the field itself is optional here.
    //Leaving it blank means "I will fill this in later"; leaving a model code in
    //it means the record will carry something that looks like an answer and is
    //not. The two are not the same kind of incomplete.
    if (step === 5 && modelCodeOnly) return false;
    if (step === 6) {
      const priceOk = !!purchasePrice && parseFloat(purchasePrice) > 0;
      const rateOk  = purchaseCurrency === "UGX" || (!!exchangeRate && parseFloat(exchangeRate) > 0);
      return priceOk && rateOk;
    }
    //A yard vehicle has no demurrage field, so gating Continue on one would be a
    //dead end: the button refuses, and the field it is refusing over is not on
    //the screen to fill in.
    if (step === 7) {
      if (!bondName.trim()) return false;
      if (!showDemurrage) return true;
      return !!dailyDemurrage && parseFloat(dailyDemurrage) >= 0;
    }
    return true;
  };

  // ── Step renderers ─────────────────────────────────────────────────────────

  const renderStep5 = () => (
    <div className="overflow-y-auto max-h-80 pr-1 flex flex-col gap-3">
      <p className="text-xs -mt-2" style={{ color: "var(--theme-muted)" }}>All fields are optional — fill what you know now.</p>

      {/* Asked HERE, on the last screen that still knows the model code. Once
          this modal hands off, that context is gone and the chassis has to be
          typed out in full against a form that cannot tell a model code from a
          chassis number.

          `required={false}` and no gate on Continue, because this step is
          explicitly "fill what you know now" — the field is required by
          AddEditInventory, which is where it is enforced. What this screen
          removes is the TYPING and the chance of getting it wrong, not the
          option to leave it for later. */}
      <ChassisComposer
        prefix={chassisPrefix} value={chassisNo} onSet={setChassisNo}
        idPrefix="pick" required={false}
        hint="As it appears on the logbook, e.g. NZE141G-9185659."
        renderInput={({ id, value }) => (
          <input id={id} type="text" className="input-t" placeholder="e.g. NZE141G-9185659"
                 value={value} onChange={(e) => setChassisNo(e.target.value)} />
        )} />
      <ModelCodeWarning hit={modelCodeOnly} />

      {/* Steering toggle */}
      <FieldRow label="Steering">
        <div className="flex gap-2 mt-0.5">
          {["RHD", "LHD"].map(opt => (
            <button
              key={opt}
              onClick={() => setSteering(opt)}
              className="flex-1 py-1.5 rounded-lg text-sm font-medium border transition-colors"
              style={steering === opt
                ? { background: "var(--theme-accent-tint)", borderColor: "var(--theme-accent-border)", color: "var(--theme-accent-ink)" }
                : { background: "transparent", borderColor: "var(--theme-line)", color: "var(--theme-muted)" }}
            >
              {opt}
            </button>
          ))}
        </div>
      </FieldRow>

      <div className="grid grid-cols-2 gap-3">
        <FieldRow label="Color">
          <input type="text" value={color} onChange={e => setColor(e.target.value)}
            placeholder="e.g. White" className="input-t" />
        </FieldRow>
        <FieldRow label="Mileage (km)">
          <input type="number" value={mileage} onChange={e => setMileage(e.target.value)}
            placeholder="e.g. 45000" min="0" className="input-t" />
        </FieldRow>
        <FieldRow label="Number Plate">
          <input type="text" value={numberPlate} onChange={e => setNumberPlate(e.target.value)}
            placeholder="e.g. AB1234NA" className="input-t" />
        </FieldRow>
      </div>

      {/* Features / Options picker */}
      <div className="pt-3" style={{ borderTop: "1px solid var(--theme-line)" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium" style={{ color: "var(--theme-fg)" }}>
            Features &amp; Options
            <span className="font-normal ml-1" style={{ color: "var(--theme-muted)" }}>(opt.)</span>
          </span>
          {selectedFeatures.length > 0 && (
            <span className="text-xs font-medium" style={{ color: "var(--theme-accent-ink)" }}>
              {selectedFeatures.length} selected
            </span>
          )}
        </div>

        {/* Category tabs */}
        <div className="flex gap-1.5 mb-2 flex-wrap">
          {Object.keys(FEATURES).map(cat => {
            const count = FEATURES[cat].filter(f => selectedFeatures.includes(f)).length;
            const active = featureTab === cat;
            return (
              <button
                key={cat}
                onClick={() => setFeatureTab(cat)}
                className="px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors"
                style={active
                  ? { background: "var(--theme-accent-tint)", borderColor: "var(--theme-accent-border)", color: "var(--theme-accent-ink)" }
                  : { background: "transparent", borderColor: "var(--theme-line)", color: "var(--theme-muted)" }}
              >
                {cat}
                {count > 0 && (
                  <span
                    className="ml-1 px-1 rounded-full text-xs"
                    style={active
                      ? { background: "var(--theme-accent-border)", color: "var(--theme-accent-ink)" }
                      : { background: "var(--theme-line)", color: "var(--theme-muted)" }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Feature chips */}
        <div className="flex flex-wrap gap-1.5 min-h-[52px]">
          {FEATURES[featureTab].map(f => {
            const selected = selectedFeatures.includes(f);
            return (
              <button
                key={f}
                onClick={() => toggleFeature(f)}
                className="px-2.5 py-1 rounded-full text-xs border transition-all"
                style={selected
                  ? { background: "var(--theme-accent)", borderColor: "var(--theme-accent)", color: "var(--theme-accent-fg)" }
                  : { borderColor: "var(--theme-line)", color: "var(--theme-fg)" }}
              >
                {f}
              </button>
            );
          })}
        </div>

        {/* Other / free-text */}
        <div className="mt-2">
          <input
            type="text"
            value={otherFeatures}
            onChange={e => setOtherFeatures(e.target.value)}
            placeholder="Other features, e.g. Bull Bar, Diff. Lock…"
            className="input-t"
          />
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <FieldRow label="Purchase Date" required>
          <input type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)}
            className="input-t" />
        </FieldRow>
        <FieldRow label="Invoice No">
          <input type="text" value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)}
            placeholder="e.g. INV-0001" className="input-t" />
        </FieldRow>
      </div>

      {/* Currency toggle */}
      <FieldRow label="Currency" required>
        <div className="flex gap-2 mt-0.5">
          {["UGX", "USD"].map(c => (
            <button
              key={c}
              onClick={() => { setPurchaseCurrency(c); if (c === "UGX") setExchangeRate("1"); }}
              className="flex-1 py-1.5 rounded-lg text-sm font-medium border transition-colors"
              style={purchaseCurrency === c
                ? { background: "var(--theme-accent-tint)", borderColor: "var(--theme-accent-border)", color: "var(--theme-accent-ink)" }
                : { background: "transparent", borderColor: "var(--theme-line)", color: "var(--theme-muted)" }}
            >
              {c}
            </button>
          ))}
        </div>
      </FieldRow>

      <div className={`grid gap-3 ${purchaseCurrency !== "UGX" ? "grid-cols-2" : "grid-cols-1"}`}>
        <FieldRow label="Purchase Price" required>
          <input
            type="number"
            value={purchasePrice}
            onChange={e => setPurchasePrice(e.target.value)}
            placeholder={purchaseCurrency === "UGX" ? "e.g. 15,000,000" : "e.g. 4,200"}
            min="0"
            autoFocus
            className="input-t"
          />
        </FieldRow>
        {purchaseCurrency !== "UGX" && (
          <FieldRow label="Exchange Rate" required>
            <input type="number" value={exchangeRate} onChange={e => setExchangeRate(e.target.value)}
              placeholder="e.g. 3700" min="0" className="input-t" />
          </FieldRow>
        )}
      </div>

      {step === 6 && !canContinue() && (() => {
        const priceOk = !!purchasePrice && parseFloat(purchasePrice) > 0;
        const rateOk  = purchaseCurrency === "UGX" || (!!exchangeRate && parseFloat(exchangeRate) > 0);
        if (!priceOk && purchasePrice !== "")  return <p className="error-t">Purchase price must be greater than zero.</p>;
        if (!priceOk)                          return <p className="hint-t">Enter the purchase price to continue.</p>;
        if (!rateOk)                           return <p className="error-t">Exchange rate is required for non-UGX purchases.</p>;
        return null;
      })()}
    </div>
  );

  const renderStep7 = () => (
    <div className="overflow-y-auto max-h-72 pr-1">
      {(!bondName.trim() || (showDemurrage && !dailyDemurrage)) && (
        <p className="error-t mb-3">
          {showDemurrage
            ? "Location and Daily Demurrage are required to continue."
            : "Location is required to continue."}
        </p>
      )}
      <div className="grid grid-cols-2 gap-3">
        {/* Required fields first */}
        <FieldRow label="Location" required>
          {/* Suggestions, not a fixed list: the vehicle may sit somewhere the
              dealership never added to its settings. */}
          <input type="text" value={bondName} onChange={e => setBondName(e.target.value)}
            list="picker-location-suggestions"
            placeholder={showDemurrage ? "e.g. KCCA Bond" : "e.g. Kireka Yard"}
            className="input-t" autoFocus />
          {locationSuggestions.length > 0 && (
            <datalist id="picker-location-suggestions">
              {locationSuggestions.map((n) => <option key={n} value={n} />)}
            </datalist>
          )}
        </FieldRow>
        {/* Asked only of a dealership that uses both kinds; otherwise seeded. */}
        {asksStorageKind && (
          <FieldRow label="Storage" required>
            <select value={storageType} onChange={e => setStorageType(e.target.value)} className="input-t">
              <option value="Bond">Bonded warehouse</option>
              <option value="Yard">Our own yard</option>
            </select>
          </FieldRow>
        )}
        {/* No bill in the dealership's own yard, so no rate to ask for. */}
        {showDemurrage && (
        <FieldRow label="Daily Demurrage" required>
          <input type="number" value={dailyDemurrage} onChange={e => setDailyDemurrage(e.target.value)}
            placeholder="e.g. 5" min="0" className="input-t" />
        </FieldRow>
        )}

        {/* Optional fields */}
        <FieldRow label="Warehouse Date">
          <input type="date" value={warehouseDate} onChange={e => setWarehouseDate(e.target.value)}
            className="input-t" />
        </FieldRow>
        <FieldRow label="BL Number">
          <input type="text" value={blNo} onChange={e => setBlNo(e.target.value)}
            placeholder="e.g. OOLU123456" className="input-t" />
        </FieldRow>
        <FieldRow label="Shipping Date">
          <input type="date" value={shippingDate} onChange={e => setShippingDate(e.target.value)}
            className="input-t" />
        </FieldRow>
        <FieldRow label="Shipping Cost (USD)">
          <input type="number" value={shippingCost} onChange={e => setShippingCost(e.target.value)}
            placeholder="e.g. 850" min="0" className="input-t" />
        </FieldRow>
        <FieldRow label="ETA Port">
          <input type="date" value={etaMombasa} onChange={e => setEtaMombasa(e.target.value)}
            className="input-t" />
        </FieldRow>
        <FieldRow label="Inland Transport (USD)">
          <input type="number" value={landTranCost} onChange={e => setLandTranCost(e.target.value)}
            placeholder="e.g. 300" min="0" className="input-t" />
        </FieldRow>
        <FieldRow label="ETA Kampala">
          <input type="date" value={etaKla} onChange={e => setEtaKla(e.target.value)}
            className="input-t" />
        </FieldRow>
        <FieldRow label="Clearing Cost MBA (USD)">
          <input type="number" value={clearingCost} onChange={e => setClearingCost(e.target.value)}
            placeholder="e.g. 200" min="0" className="input-t" />
        </FieldRow>
        <FieldRow label="Clearing Cost KLA (UGX)">
          <input type="number" value={clearingUG} onChange={e => setClearingUG(e.target.value)}
            placeholder="e.g. 500,000" min="0" className="input-t" />
        </FieldRow>
        <FieldRow label="Duty (UGX)">
          <input type="number" value={duty} onChange={e => setDuty(e.target.value)}
            placeholder="e.g. 3,000,000" min="0" className="input-t" />
        </FieldRow>
        <FieldRow label="Target Export Price">
          <input type="number" value={targetExportPrice} onChange={e => setTargetExportPrice(e.target.value)}
            placeholder="e.g. 5,500" min="0" className="input-t" />
        </FieldRow>
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  const modal = (
    <Theme>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="absolute inset-0"
          style={{ background: "rgba(15, 23, 42, 0.55)" }}
          onClick={onClose}
        />
        {/* .modal-viewport-cap: never outgrow the viewport — the body scrolls instead */}
        <div
          id={TOUR_ANCHORS.vehiclePickerModal}
          className="relative w-full max-w-lg mx-4 overflow-hidden flex flex-col modal-viewport-cap"
          style={{
            background: "var(--theme-surface)",
            borderRadius: "var(--theme-radius-lg)",
            boxShadow: "var(--theme-shadow-dialog)",
          }}
        >
          {/* Header */}
          <ModalTitleBar
            icon={Car}
            title={`${stepTitles[step - 1]} · New Vehicle (${origin})`}
            onClose={onClose}
          />

          {/* Body */}
          <div className="px-4 py-4 md:px-8 md:py-6 flex-1 min-h-0 overflow-y-auto">
            <StepIndicator step={step} total={totalSteps} />
            {error && (
              <div
                className="mb-3 px-4 py-2 rounded-lg text-sm"
                style={{
                  background: "var(--theme-danger-tint)",
                  border: "1px solid var(--theme-danger)",
                  color: "var(--theme-danger-ink)",
                }}
              >
                {error}
              </div>
            )}
            {step <= 4 && <VehicleIdentitySteps step={step} identity={identity} />}
            {step === 5 && renderStep5()}
            {step === 6 && renderStep6()}
            {step === 7 && renderStep7()}
          </div>

          {/* Footer */}
          <div className="modal-shell-footer" style={{ justifyContent: "space-between" }}>
            <button
              id={TOUR_ANCHORS.vehiclePickerEnterManually}
              onClick={onSkip}
              className="link-t text-sm"
              style={{ textDecoration: "underline", textUnderlineOffset: "2px" }}
            >
              Enter manually
            </button>
            <div className="flex gap-2">
              {step > 1 && (
                <button
                  onClick={goBack}
                  className="btn-t btn-t-secondary flex items-center gap-1"
                >
                  <ChevronLeft size={14} /> Back
                </button>
              )}
              {step >= 4 && (
                <button
                  onClick={handleContinue}
                  disabled={!canContinue()}
                  className="btn-t btn-t-primary flex items-center gap-1"
                >
                  {step === totalSteps ? "Finish" : "Continue"} <ChevronRight size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Theme>
  );

  return ReactDOM.createPortal(modal, document.body);
}
